import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  BasicTextEditor,
  BasicTextEditorHandle,
  Spinner,
  StatusTag,
  Switch,
} from '@factorialco/factorial-one-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { useBasicTextEditorConfig } from 'modules/meetings/lib/hooks/useBasicTextEditorConfig'
import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useMeetingNotes } from 'modules/meetings/lib/hooks/useMeetingNotes'
import { formatDateTimeToString } from 'modules/meetings/lib/utils/dates'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

interface NotesProps {
  liveCompanionEnabled: boolean
  setLiveCompanionEnabled: (value: boolean) => void
  loadingLiveCompanion: boolean
  textEditorRef: React.RefObject<BasicTextEditorHandle>
}

export const Notes = ({
  liveCompanionEnabled,
  setLiveCompanionEnabled,
  loadingLiveCompanion,
  textEditorRef,
}: NotesProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { id: meetingId } = useParams<{ id: string }>()
  const { notes, setNotes, isSavingNotes } = useMeetingNotes()
  const { page } = useMeetingDetails()

  const { labels, aiBlockConfig } = useBasicTextEditorConfig({
    meetingId,
    meetingState: 'post',
    setNotes,
  })

  return (
    <Box
      flexDirection="column"
      height="full"
      flexGrow
      position="relative"
      style={{
        cursor: 'text',
      }}
      overflowY="auto"
    >
      <Box
        alignItems="center"
        justifyContent="spaceBetween"
        flexDirection="row"
        paddingX="s24"
        paddingTop="s12"
        paddingBottom="s8"
      >
        <StatusTag text={i18n.t('meetings.notes.private_notes')} variant="neutral" />
        {page?.updatedAt && (
          <Text size="100" color="grey800">
            {isSavingNotes
              ? i18n.t('meetings.notes.autosaving')
              : formatDateTimeToString(page?.updatedAt, locale)}
          </Text>
        )}
      </Box>

      {loadingLiveCompanion && (
        <Box
          alignItems="center"
          justifyContent="center"
          position="absolute"
          height="full"
          background="grey100"
          gap="s12"
          style={{
            zIndex: '1000',
            width: '100%',
            top: '50%',
            left: '50%',
            opacity: '0.5',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Text.Normal>{i18n.t('meetings.room.live_companion.autofilling')}</Text.Normal>
          <Spinner />
        </Box>
      )}

      <Box paddingRight="s24" flexGrow className={styles.hiddenScrollBar}>
        <BasicTextEditor
          readonly={loadingLiveCompanion}
          ref={textEditorRef}
          labels={labels}
          placeholder={i18n.t('meetings.notes.placeholder')}
          onChange={({ json }) => {
            setNotes(JSON.stringify(json))
          }}
          initialEditorState={{ content: notes ? JSON.parse(notes) : null }}
          aiBlockConfig={aiBlockConfig}
        />
      </Box>

      <Box paddingX="s24" paddingBottom="s12" paddingTop="s8">
        <Switch
          title={i18n.t('meetings.room.live_companion.title')}
          checked={liveCompanionEnabled}
          onCheckedChange={setLiveCompanionEnabled}
        />
      </Box>
    </Box>
  )
}
